import { createContext, useContext, useEffect, useState } from "react";
import { getLoadedAuth, loadAuth, whenAuthLoaded } from "../utils/firebaseAuth";
import nookies from "nookies";
import axios from "axios";

const AuthContext = createContext();

const fetchUser = async (uid, token) => {
  return await axios
    .get(`/api/user`, {
      headers: {
        Authorization: token,
      },
    })
    .then((res) => res.data);
};

// The profile saved by the last confirmed session. A returning visitor sees
// the signed-in header from it at once, before the Firebase SDK (loaded on
// demand, see utils/firebaseAuth) has confirmed the session; Firebase's
// answer then replaces it, or clears it if the session has ended.
const readCachedUser = () => {
  try {
    const raw = localStorage.getItem("user");
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

// Run `fn` when the main thread is idle (at the latest after `timeout` ms).
// Safari has no requestIdleCallback.
const whenIdle = (fn, timeout) => {
  if (typeof window.requestIdleCallback === "function") {
    const id = window.requestIdleCallback(fn, { timeout });
    return () => window.cancelIdleCallback(id);
  }
  const id = setTimeout(fn, Math.min(timeout, 1500));
  return () => clearTimeout(id);
};

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const onIdTokenChanged = async (firebaseUser) => {
      setLoading(true);
      if (!firebaseUser) {
        setUser(null);
        nookies.set(undefined, "token", "", { path: "/" });
        localStorage.removeItem("user");
      } else {
        const token = await firebaseUser.getIdToken();
        let userData = null;
        let attempts = 0;
        while (attempts < 8) {
          try {
            userData = await fetchUser(firebaseUser.uid, token);
            break;
          } catch (error) {
            console.error("Error fetching user data:", error);
            attempts++;
            if (attempts < 8) {
              await new Promise((resolve) => setTimeout(resolve, 2000));
            }
          }
        }
        if (!userData) {
          console.error("Failed to fetch user data after 3 attempts");
          setLoading(false);
          return;
        }
        const updatedUser = {
          ...firebaseUser,
          ...userData,
        };
        setUser(updatedUser);
        localStorage.setItem("user", JSON.stringify(updatedUser));
        nookies.set(undefined, "token", token, { path: "/" });
        sessionStorage.setItem("authToken", token);
      }
      setLoading(false);
    };

    // Subscribe as soon as anything loads the SDK: this effect, or the
    // sign-in modal / an authed request getting there first.
    let unsubscribe = () => {};
    const stopWaiting = whenAuthLoaded((auth) => {
      unsubscribe = auth.onIdTokenChanged(onIdTokenChanged);
    });

    const load = () =>
      loadAuth().catch((error) => {
        console.error("Error loading Firebase Auth:", error);
        setLoading(false);
      });
    let cancelIdle = () => {};
    const cachedUser = readCachedUser();
    if (cachedUser) {
      // Returning visitor: show them signed in and confirm straight away;
      // the page's authed requests are waiting on this.
      setUser(cachedUser);
      load();
    } else {
      // Probably logged out: nothing needs Firebase until they act (which
      // loads it at once), so fetch it when the page has settled.
      cancelIdle = whenIdle(load, 3000);
    }

    return () => {
      stopWaiting();
      unsubscribe();
      cancelIdle();
    };
  }, []);

  // Token refresh logic
  useEffect(() => {
    const handle = setInterval(
      async () => {
        const user = getLoadedAuth()?.currentUser;
        if (user) {
          const token = await user.getIdToken(true);
          nookies.set(undefined, "token", token, { path: "/" });
          sessionStorage.setItem("authToken", token);
        }
      },
      10 * 60 * 1000,
    ); // Corrected to 10 minutes

    return () => clearInterval(handle);
  }, []);

  const updateUser = (updatedUserData) => {
    setUser((prevUser) => {
      const newUser = { ...prevUser, ...updatedUserData };
      localStorage.setItem("user", JSON.stringify(newUser));
      return newUser;
    });
  };

  return (
    <AuthContext.Provider value={{ user, setUser, updateUser, loading }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
